import { datadogAddAction, getDatadogInstance } from '@cw-elements/analytics-provider';
import { analyticsReady } from 'cropwise_accounts/analytics';

/**
 * Reports the user-facing alerts the Cropwise Accounts pages render server-side.
 *
 * Since these pages became server-rendered there is no XHR for RUM to flag: a
 * rejected form is a 302 followed by a normal page view, so a grower blocked by
 * validation looks exactly like one who succeeded. The server names the cause in
 * `data-error-reason` (see WithSignupErrorRedirects) and this forwards it.
 *
 * Validation the visitor can correct is reported as an action — it is the product
 * working as designed, and belongs in funnels rather than in Error Tracking, where
 * it would open issues, inflate session error counts, and eventually be muted.
 * Only an unexpected failure is reported as an error.
 */

export const USER_ERROR_ACTION = 'accounts user error';
// Any element may declare the reason: signup hangs it on the shared alert banner,
// while sign-in renders its failures in the form and uses a carrier of its own.
const REASON_SELECTOR = '[data-error-reason]';
const DEFECT_REASONS = new Set(['generic_error']);

// Alerts outside the signup flow carry no reason yet, and reporting them as
// "undefined" would be a facet nobody can act on.
export const reportTargetFor = (reason) => {
  if (!reason) return null;

  return DEFECT_REASONS.has(reason) ? 'error' : 'action';
};

// Error Tracking fingerprints on the message, so it carries the reason rather
// than the alert text, which differs across 35 locales.
export const defectMessage = (reason) => `${USER_ERROR_ACTION}: ${reason}`;

const report = async (target, reason) => {
  await analyticsReady;

  if (target === 'error') {
    getDatadogInstance()?.addError(new Error(defectMessage(reason)), { reason });
    return;
  }

  datadogAddAction(USER_ERROR_ACTION, { reason });
};

// The DOM is read now — the alert is on the page this instant — while the report
// waits for the provider, which drops anything sent before it finishes starting.
export const initUserErrorEvents = () => {
  const reason = document.querySelector(REASON_SELECTOR)?.dataset.errorReason;
  const target = reportTargetFor(reason);
  if (!target) return;

  void (async () => {
    try {
      await report(target, reason);
    } catch (error) {
      // Background failures must be reported without rejecting the event handler.
      console.error(error); // eslint-disable-line no-console
    }
  })();
};
